import { CerFormAdapter } from '@form/cer-form-adapter';
import { CerFormViewSet } from '@form/cer-form-view-set';
import { CerFormView } from '@form/cer-form-view';
import { CerFormPanesSettingsUtil } from '@form/cer-form-panes/cer-form-panes.service';
import { UiActionTypeEnum, UiCommand, UiCommandEvent } from '@ui/cer-ui-types';

import { CerGridComponent } from 'src/cer/cer-grid/cer-grid.component';
import { FieldWidthEnum, ViewMetadata } from 'src/cer/cer-data/cer-data-types';
import { formatDateDifference } from 'src/cer/cer-util/cer-date';
import { OnHandCellInfo } from './warehouse-onhand-status.form';

export class WarehouseOutboundBackOrderOnHandStatusForm extends CerFormAdapter {

  public override viewSetInit(viewSet: CerFormViewSet) {

    if (this.routeOptions.routeData['hasDetails'] == true) {
      viewSet.panes = CerFormPanesSettingsUtil.masterDetail('vertical', true, 30);

      const v = this.viewGet();
      v.title = 'Restordrer';
      viewSet.viewAdd(v);

      const vDetail = this.viewGetDetails(v);
      vDetail.title = "Linjer";
      viewSet.viewAdd(vDetail, v);
    }
    else {
      const v = this.viewGet();
      v.title = 'Restordrelinjer';
      viewSet.viewAdd(v);
    }
  }

  private static viewMetadata(): ViewMetadata {
    return {
      name: 'lager-restordrer', dataApiName: 'WarehouseOutboundBackOrderOnHandProductStatus',
      primaryKey: 'warehouseId', allowEdit: false, allowCreate: false, allowDelete: false
    };
  }

  private viewGet(): CerFormView {
    const v: CerFormView = new CerFormView();
    const productVisible = !(this.routeOptions?.navigation?.columnNameChild == 'product');

    v.toolbarCommands = [this.updatedDateCmd];
    v.viewMetadata = WarehouseOutboundBackOrderOnHandStatusForm.viewMetadata();
    v.onCommandEvent = (e => this.onCommand(e));
    v.fieldMetadata = [
      { name: 'requested', text: 'Ønsket dato', format: 'DateShort', orderByPriority: 1, orderBy: 'Descending' },
      { name: 'outboundOrderNum', text: 'Forsendelse', 
        foreignKeyType: 'warehouseOutboundOrderLookup',
        orderByPriority: 2, orderBy: 'Descending', filterOnClient: true, filterOperator: 'notequal', filterValue: '' },
      { name: 'purchOrderFormNum', text: 'Ordrenr', orderByPriority: 3, orderBy: 'Descending'},
      { name: 'customerRef', text: 'Reference', visible: false },
      { name: 'warehouseCounterpart', text: 'Lager kundenr.', visible: false },
      { name: 'pickOrderNum', text: 'Lager ordre', visible: false, orderByPriority: 4, orderBy: 'Descending' },
      { name: 'product', text: 'ISBN', foreignKeyType: 'productLookup', visible: productVisible },
      { name: 'name', text: 'Titel', widthType: FieldWidthEnum.XXL, visible: productVisible },
      { name: 'name2', text: 'Undertitel', visible: false },
      { name: 'seriesId', text: 'Serie', visible: false },
      { name: 'group', text: 'Gruppe', visible: false, foreignKeyType: 'productGroup' },
      { name: 'bookstoreClassification', text: 'Varegruppe', visible: false, foreignKeyType: 'productCategory' },
      { name: 'categories', text: 'Kategorier', visible: false },
      { name: 'warehouseItemNum', text: 'Lager varenr.', visible: false },
      { name: 'orderedQty', text: 'Bestilt antal', format: 'Number', filterOnClient: true, filterOperator: 'notequal', filterValue: 0 },
      { name: 'remainQty', text: 'Ej leveret antal', format: 'Number' },
      { name: 'reservedPhysicalQty', text: 'Reserveret antal', format: 'Number' },
      { name: 'unreservedQty', text: 'Ej reserveret antal', format: 'Number', onQueryCellInfo: OnHandCellInfo.availableQty },
      { name: 'availableQty', text: 'Lager ej reserveret', format: 'Number', onQueryCellInfo: OnHandCellInfo.availableQty },
      { name: 'reservedQty', text: 'Lgaer reserveret', format: 'Number', visible: false },
      { name: 'physicalQty', text: 'Lager fysisk', format: 'Number', },
      { name: 'orderNumAlt', text: 'Ordrenr. alternativ', visible: false },
      { name: 'updated', text: 'Opdateret', format: 'DateShort', visible: false },
      { name: 'created', text: 'Rest opdateret', format: 'DateShort', visible: false },
      { name: 'released', text: 'Udgivet dato', format: 'DateShort', visible: false },
      { name: 'preorder', text: 'Forudbestilling', format: 'CheckBox', width: '60px' },
      { name: 'published', text: 'Udgivet', format: 'CheckBox', visible: false },
      { name: 'buyable', text: 'Lagerføres', format: 'CheckBox' },
      { name: 'buyableTrade', text: 'Kan købes', format: 'CheckBox' },
      { name: 'shreddedYear', text: 'Markuleret', format: 'Integer', visible: false },
      { name: 'forceVisible', text: 'Tving synlig', format: 'CheckBox', visible: false },
      { name: 'forceBuyable', text: 'Tving kan købes', format: 'CheckBox', visible: false },
      { name: 'publishedDate', text: 'Udgivet dato', format: 'DateShort', visible: false, onQueryCellInfo: OnHandCellInfo.publishedDate },
      { name: 'publishedDays', text: 'Udgivet dage', tooltipText: 'Antal dage udgivet fra Bogportalen', format: 'Integer', visible: false, onQueryCellInfo: OnHandCellInfo.publishedDate },
      { name: 'printPublishedDate', text: 'Udgivet bogportal print', format: 'DateShort', visible: false },
      { name: 'availableForPurchaseDate', text: 'Tilgængelig bogportal køb', format: 'DateShort', visible: false },
      { name: 'statusCode', text: 'Status', visible: false },
      { name: 'onHandUpdated', text: 'Lager opdateret ', format: 'DateShort', visible: false },
      { name: 'approximateInventoryNumber', text: 'Ca. lager', format: 'TextRight', visible: false },
      { name: 'minimumInventory', text: 'Min. lager', format: 'Integer', visible: false },
      { name: 'apologyCode', text: 'Undskyldningskode', format: 'Integer', widthType: FieldWidthEnum.XS, onQueryCellInfo: OnHandCellInfo.apologyCode, visible: false },
      { name: 'salesPrice', text: 'Salgspris', format: 'Integer', visible: false },
      { name: 'modifiedJobId', text: 'Rest opdateret job', format: 'Number', visible: false }
    ];

    return v;
  }

  private viewGetDetails(parent: CerFormView) {
    const v: CerFormView = new CerFormView();
    v.viewMetadata = Object.assign({}, parent.viewMetadata);
    v.viewMetadata.parentPrimaryKey = 'pickOrderNum';
    v.viewMetadata.parentForeignKey = 'pickOrderNum';

    parent.viewMetadata.dataApiName = 'WarehouseOutboundBackOrderStatus';
    parent.viewMetadata.primaryKey = 'pickOrderNum';

    v.fieldMetadata = [];
    parent.fieldMetadata.forEach(f => v.fieldMetadata.push(Object.assign({}, f)));
    v.fieldMetadata.forEach(f => { f.filterValue = null; f.filterOperator = null; });

    var bothFields = ['orderedQty', 'remainQty', 'reservedPhysicalQty', 'unreservedQty'];
    var detailOnlyFields = ['id', 'warehouseId', 'released', 'published', 'buyable', 'shreddedYear', 'forceVisible', 'forceBuyable', 'publishedDate', 'printPublishedDate', 'availableForPurchaseDate',
      'statusCode', 'product', 'name', 'name2', 'seriesId', 'bookstoreClassification', 'categories', 'warehouseItemNum',
      'availableQty', 'reservedQty', 'physicalQty', 'orderNumAlt', 'created', 'modifiedJobId', 'onHandUpdated', 'approximateInventoryNumber', 'minimumInventory', 'apologyCode',
      'exchangePool', 'salesPrice'];
    parent.fieldMetadata = parent.fieldMetadata.filter(f => bothFields.includes(f.name) || !detailOnlyFields.includes(f.name));
    v.fieldMetadata.filter(f => !bothFields.includes(f.name) && !detailOnlyFields.includes(f.name)).forEach(f => f.visible = false);
    v.fieldMetadata.filter(f => f.name == 'unreservedQty').forEach(f => { f.orderBy = 'Descending'; f.orderByPriority = 1; });

    return v;
  }

  public onCommand(event: UiCommandEvent) {
    if (event.actionType == UiActionTypeEnum.DataBound) {
      var data: any[] = event.args?.result;
      if (event.appComponent && data && data.length > 0) {
        this.setupUpdatedDate(<CerGridComponent>event.appComponent, new Date(data[0].created));
      }
    }
  }

  private updatedDateCmd: UiCommand = {
    id: 'updatedDate', text: 'Opdateret: ', disabled: true, align: 'right',
    iconCss: "e-menu-icon e-icons e-circle-check", tooltipText: 'Dato hvor data er opdateret', target: ".e-content", visible: true
  };

  private setupUpdatedDate(grid: CerGridComponent, updatedDate: Date) {
    var text: string = 'Opdateret ' + formatDateDifference(updatedDate, new Date());
    var button: HTMLButtonElement = grid.commandService.toolbarButtonGet(this.updatedDateCmd.tooltipText);
    if (button) {
      grid.commandService.toolbarButtonTextSet(button, text);
    }
  }
}
